import React, { useState } from 'react';
import apiService from '../../../services/api';

const NBFCCalculatorModule = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    amount: '',
    days: '',
    mobile: ''
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === 'mobile') {
      // Only allow digits, max 10
      processedValue = value.replace(/\D/g, '').slice(0, 10);
    } else if (name === 'amount') {
      // Allow numbers and one decimal point
      processedValue = value.replace(/[^\d.]/g, '');
      // Ensure only one decimal point
      const parts = processedValue.split('.');
      if (parts.length > 2) {
        processedValue = parts[0] + '.' + parts.slice(1).join('');
      }
      // Limit decimal places to 2
      if (parts[1] && parts[1].length > 2) {
        processedValue = parts[0] + '.' + parts[1].slice(0, 2);
      }
    } else if (name === 'days') {
      // Only allow positive integers
      processedValue = value.replace(/\D/g, '');
    }
    
    setFormData({
      ...formData,
      [name]: processedValue
    });
  };

  // Calculate interest locally (fallback if API fails)
  const calculateInterestLocal = (amount, days) => {
    const interestRate = 14; // Fixed annual interest rate of 14%
    const rateDecimal = interestRate / 100; // 14% -> 0.14
    const interestAmount = (amount * rateDecimal * days) / 365;
    const totalAmount = amount + interestAmount;
    
    // Round to 2 decimal places
    const interest = Math.round(interestAmount * 100) / 100;
    const total = Math.round(totalAmount * 100) / 100;
    
    return {
      success: true,
      interest: interest,
      total_amount: total,
      principal: amount,
      days: days,
      interest_rate: interestRate,
      currency: 'INR'
    };
  };

  const calculateInterest = async () => {
    // Validate inputs
    if (!formData.amount || !formData.days) {
      alert('Please fill all required fields');
      return;
    }

    const amount = parseFloat(formData.amount);
    const days = parseInt(formData.days);

    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid amount greater than 0');
      return;
    }

    if (isNaN(days) || days <= 0) {
      alert('Please enter valid credit days greater than 0');
      return;
    }

    setLoading(true);
    try {
      // Try to call API first
      const data = await apiService.calculateNBFC(amount, days);
      
      if (data && data.success) {
        setResults(data);
        setStep(2); // Move to mobile number step
      } else {
        // If API fails, calculate locally as fallback
        console.warn('API call failed, using local calculation:', data?.message);
        const localResult = calculateInterestLocal(amount, days);
        setResults(localResult);
        setStep(2); // Move to mobile number step
      }
    } catch (error) {
      console.error('Error calculating interest:', error);
      // If API fails completely, calculate locally as fallback
      console.warn('API error, using local calculation as fallback');
      const localResult = calculateInterestLocal(amount, days);
      setResults(localResult);
      setStep(2); // Move to mobile number step
    } finally {
      setLoading(false);
    }
  };

  const submitMobileAndShowResults = async () => {
    if (!formData.mobile || formData.mobile.length !== 10) {
      alert('Please enter a valid 10-digit mobile number');
      return;
    }

    const purchaseAmount = parseFloat(formData.amount) || 0;
    const creditDays = parseInt(formData.days, 10) || 0;
    const interestRate = results?.interest_rate != null ? results.interest_rate : 14;
    const approxInterest = results?.interest != null ? parseFloat(results.interest) : 0;
    const totalPayable = results?.total_amount != null ? parseFloat(results.total_amount) : purchaseAmount;

    try {
      const res = await apiService.saveCreditInterest({
        purchase_amount: purchaseAmount,
        required_credit_days: creditDays,
        interest_rate: interestRate,
        mobile: formData.mobile.replace(/\D/g, '').slice(0, 10),
        amount: purchaseAmount,
        approx_interest: approxInterest,
        total_payable_amount: totalPayable
      });
      if (res && !res.success && res.message) {
        alert(res.message);
      }
    } catch (error) {
      console.error('Error saving credit interest:', error);
      alert('Could not submit. Please try again.');
    }

    setStep(3);
  };

  const handleReset = () => {
    setStep(1);
    setFormData({ amount: '', days: '', mobile: '' });
    setResults(null);
  };

  return (
    <div className="module-card nbfc-module">
      <div className="module-header">
        <div className="module-icon">
          <i className="fas fa-calculator"></i>
        </div>
        <h3>Credit Interest Calculator</h3>
      </div>
      <div className="calculator-mini-form" style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '0', overflowY: 'auto', flex: 1, minHeight: 0 }}>
        {step === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', flex: 1 }}>
            <div className="form-field-mini">
              <label>Purchase amount (₹)</label>
              <input 
                type="text" 
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="Enter purchase amount" 
                required 
                inputMode="decimal"
              />
            </div>
            <div className="form-field-mini">
              <label>Required credit days</label>
              <input 
                type="text" 
                name="days"
                value={formData.days}
                onChange={handleChange}
                placeholder="Enter credit days" 
                required 
                inputMode="numeric"
              />
            </div>
            <div className="form-field-mini">
              <label>Interest Rate</label>
              <input 
                type="text" 
                value="14%" 
                readOnly 
                style={{ backgroundColor: '#f5f5f5', color: '#064E8D', fontWeight: 600, cursor: 'default' }}
              />
            </div>
            <div className="nbfc-info">
              <div className="info-item">
                <i className="fas fa-percentage"></i>
                <span>Competitive Rates</span>
              </div>
              <div className="info-item">
                <i className="fas fa-clock"></i>
                <span>Quick Approval</span>
              </div>
              <div className="info-item">
                <i className="fas fa-shield-alt"></i>
                <span>Secure Process</span>
              </div>
            </div>
            <button 
              className="btn btn-primary btn-calculate" 
              type="button" 
              onClick={calculateInterest}
              disabled={loading || !formData.amount || !formData.days}
            >
              {loading ? 'Calculating...' : 'Calculate Interest'}
            </button>
          </div>
        )}

        {step === 2 && results && (
          <div className="credit-step active" style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', flex: 1 }}>
            <div className="form-field-mini">
              <label>Purchase amount (₹)</label>
              <input 
                type="text" 
                value={formData.amount} 
                readOnly 
                style={{ backgroundColor: '#f5f5f5' }}
              />
            </div>
            <div className="form-field-mini">
              <label>Required credit days</label>
              <input 
                type="text" 
                value={formData.days} 
                readOnly 
                style={{ backgroundColor: '#f5f5f5' }}
              />
            </div>
            <div className="form-field-mini">
              <label>Interest Rate</label>
              <input 
                type="text" 
                value="14%" 
                readOnly 
                style={{ backgroundColor: '#f5f5f5', color: '#064E8D', fontWeight: 600 }}
              />
            </div>
            <div className="form-field-mini">
              <label>Mobile Number</label>
              <input 
                type="tel" 
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                placeholder="Enter mobile number" 
                maxLength="10" 
                required 
                inputMode="numeric"
              />
            </div>
            <button 
              className="btn btn-primary btn-calculate" 
              type="button" 
              onClick={submitMobileAndShowResults}
              disabled={formData.mobile.length !== 10}
              style={{ width: '100%', marginTop: 'auto' }}
            >
              Submit
            </button>
          </div>
        )}

        {step === 3 && results && (
          <div className="credit-step active" id="credit-step-3" style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', flex: 1, overflowY: 'auto' }}>
            <div className="form-field-mini">
              <label>Purchase amount (₹)</label>
              <input 
                type="text" 
                value={formData.amount} 
                readOnly 
                style={{ backgroundColor: '#f5f5f5' }}
              />
            </div>
            <div className="form-field-mini">
              <label>Required credit days</label>
              <input 
                type="text" 
                value={formData.days} 
                readOnly 
                style={{ backgroundColor: '#f5f5f5' }}
              />
            </div>
            <div className="form-field-mini">
              <label>Interest Rate</label>
              <input 
                type="text" 
                value="14%" 
                readOnly 
                style={{ backgroundColor: '#f5f5f5', color: '#064E8D', fontWeight: 600 }}
              />
            </div>
            <div className="form-field-mini">
              <label>Mobile Number</label>
              <input 
                type="text" 
                value={formData.mobile} 
                readOnly 
                style={{ backgroundColor: '#f5f5f5' }}
              />
            </div>
            <div className="calculator-mini-result">
              <span className="result-label">AMOUNT</span>
              <span className="result-value">₹{parseFloat(formData.amount || 0).toFixed(2)}</span>
            </div>
            <div className="calculator-mini-result">
              <span className="result-label">APPROX INTEREST</span>
              <span className="result-value">₹{results.interest ? parseFloat(results.interest).toFixed(2) : '0.00'}</span>
            </div>
            <div className="calculator-mini-result" id="mini-nbfc-total-result">
              <span className="result-label">TOTAL PAYABLE AMOUNT</span>
              <span className="result-value" id="credit-total-result">₹{results.total_amount ? parseFloat(results.total_amount).toFixed(2) : parseFloat(formData.amount || 0).toFixed(2)}</span>
            </div>
            <button 
              className="btn btn-secondary" 
              type="button" 
              onClick={handleReset}
              style={{ marginTop: 'auto' }}
            >
              Calculate Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NBFCCalculatorModule;

